'use client';

import { useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft,
  ChatCircleText,
  Bug,
  Lightbulb,
  Question,
  Star,
  PaperPlaneRight,
  CheckCircle,
  Spinner,
  Image as ImageIcon,
  X,
} from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { useToastActions } from '../components/ui/Toast';

type FeedbackType = 'suggestion' | 'bug' | 'question' | 'praise';

interface FeedbackTypeOption {
  id: FeedbackType;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string; weight?: 'thin' | 'light' | 'regular' | 'bold' | 'fill' | 'duotone' }>;
  iconColor: string;
  iconBgColor: string;
}

const feedbackTypes: FeedbackTypeOption[] = [
  {
    id: 'suggestion',
    label: '功能建议',
    description: '提出新功能或改进建议',
    icon: Lightbulb,
    iconColor: 'text-amber-600',
    iconBgColor: 'bg-amber-100',
  },
  {
    id: 'bug',
    label: '问题反馈',
    description: '报告使用中的问题或 Bug',
    icon: Bug,
    iconColor: 'text-red-600',
    iconBgColor: 'bg-red-100',
  },
  {
    id: 'question',
    label: '使用咨询',
    description: '询问使用方法或功能',
    icon: Question,
    iconColor: 'text-blue-600',
    iconBgColor: 'bg-blue-100',
  },
  {
    id: 'praise',
    label: '产品好评',
    description: '分享您的使用体验',
    icon: Star,
    iconColor: 'text-purple-600',
    iconBgColor: 'bg-purple-100',
  },
];

// 最大图片数量
const MAX_IMAGES = 4;
// 最大图片大小 (5MB)
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

export function FeedbackPage() {
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToastActions();
  const [selectedType, setSelectedType] = useState<FeedbackType>('suggestion');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [email, setEmail] = useState('');
  const [images, setImages] = useState<File[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 处理图片选择
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const newFiles: File[] = [];
    const newPreviews: string[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // 检查图片数量限制
      if (images.length + newFiles.length >= MAX_IMAGES) {
        toastError(`最多只能上传 ${MAX_IMAGES} 张图片`);
        break;
      }

      // 验证文件类型
      if (!file.type.startsWith('image/')) {
        toastError(`${file.name} 不是图片文件`);
        continue;
      }

      // 验证文件大小
      if (file.size > MAX_IMAGE_SIZE) {
        toastError(`${file.name} 超过 5MB 限制`);
        continue;
      }

      newFiles.push(file);
      newPreviews.push(URL.createObjectURL(file));
    }

    setImages(prev => [...prev, ...newFiles]);
    setImagePreviews(prev => [...prev, ...newPreviews]);

    // 清空 input 以便可以重复选择相同文件
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // 删除图片
  const handleRemoveImage = (index: number) => {
    // 释放预览 URL
    URL.revokeObjectURL(imagePreviews[index]);
    
    setImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
  };

  // 上传图片到服务器
  const uploadImages = async (): Promise<string[]> => {
    if (images.length === 0) return [];

    setIsUploading(true);
    const uploadedUrls: string[] = [];

    try {
      const token = localStorage.getItem('token') || '';
      const baseURL = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1';

      for (const image of images) {
        const formData = new FormData();
        formData.append('image', image);

        const response = await fetch(`${baseURL}/feedback/upload-image`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });

        if (!response.ok) {
          throw new Error(`上传失败: ${image.name}`);
        }

        const result = await response.json();
        if (result.code === 0) {
          uploadedUrls.push(result.data.url);
        } else {
          throw new Error(result.message || `上传失败: ${image.name}`);
        }
      }
    } catch (error) {
      console.error('Upload images error:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }

    return uploadedUrls;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim() || !content.trim()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      // 先上传图片
      let imageUrls: string[] = [];
      if (images.length > 0) {
        imageUrls = await uploadImages();
      }

      // 提交反馈
      const token = localStorage.getItem('token') || '';
      const baseURL = (import.meta as { env?: { VITE_API_BASE_URL?: string } }).env?.VITE_API_BASE_URL || '/api/v1';

      const response = await fetch(`${baseURL}/feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          type: selectedType,
          title: title.trim(),
          content: content.trim(),
          email: email.trim() || undefined,
          images: imageUrls,
        }),
      });

      if (!response.ok) {
        throw new Error('提交失败');
      }

      const result = await response.json();
      if (result.code === 0) {
        setIsSubmitted(true);
        toastSuccess('反馈提交成功，感谢您的建议！');
        
        // 清理预览 URL
        imagePreviews.forEach(url => URL.revokeObjectURL(url));
        setImages([]);
        setImagePreviews([]);
      } else {
        throw new Error(result.message || '提交失败');
      }
    } catch (error) {
      toastError(error instanceof Error ? error.message : '提交失败，请重试');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="h-full bg-zinc-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="max-w-md w-full mx-6 text-center"
        >
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-emerald-600" weight="fill" />
          </div>
          <h2 className="text-2xl font-bold text-zinc-900 mb-2">感谢您的反馈！</h2>
          <p className="text-sm text-zinc-500 mb-8">
            我们已收到您的{feedbackTypes.find(t => t.id === selectedType)?.label}，
            团队会尽快进行评估和处理。
          </p>
          <div className="space-y-3">
            <button
              onClick={() => {
                setIsSubmitted(false);
                setTitle('');
                setContent('');
                setEmail('');
                setImages([]);
                setImagePreviews([]);
              }}
              className="w-full py-3 bg-emerald-600 text-white font-medium rounded-xl hover:bg-emerald-700 transition-colors"
            >
              继续提交反馈
            </button>
            <button
              onClick={() => navigate(-1)}
              className="w-full py-3 text-zinc-600 font-medium hover:text-zinc-900 transition-colors"
            >
              返回
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-full bg-zinc-50 overflow-auto">
      <div className="max-w-2xl mx-auto p-6">
        {/* 头部 */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-zinc-200 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-zinc-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-zinc-900">用户反馈</h1>
            <p className="text-sm text-zinc-500">帮助我们改进产品</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* 反馈类型选择 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6">
            <h3 className="text-sm font-medium text-zinc-900 mb-4">反馈类型</h3>
            <div className="grid grid-cols-2 gap-3">
              {feedbackTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    type="button"
                    onClick={() => setSelectedType(type.id)}
                    className={`flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all ${
                      selectedType === type.id
                        ? 'border-emerald-500 bg-emerald-50/30'
                        : 'border-zinc-100 hover:border-zinc-200'
                    }`}
                  >
                    <div className={`w-10 h-10 rounded-lg ${type.iconBgColor} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${type.iconColor}`} weight="fill" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-zinc-900">{type.label}</p>
                      <p className="text-xs text-zinc-500 mt-0.5">{type.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* 反馈内容 */}
          <div className="bg-white rounded-2xl border border-zinc-200/50 p-6 space-y-4">
            <h3 className="text-sm font-medium text-zinc-900">反馈内容</h3>
            
            <div>
              <label className="text-sm text-zinc-600 mb-2 block">标题</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="请简要描述您的反馈"
                className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                required
              />
            </div>

            <div>
              <label className="text-sm text-zinc-600 mb-2 block">详细描述</label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="请详细描述您的反馈内容..."
                rows={6}
                maxLength={500}
                className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 resize-none"
                required
              />
              <p className="text-xs text-zinc-400 mt-2">{content.length}/500 字</p>
            </div>

            {/* 图片上传 */}
            <div>
              <label className="text-sm text-zinc-600 mb-2 block">
                相关截图 <span className="text-zinc-400">（选填，最多 {MAX_IMAGES} 张）</span>
              </label>
              
              {/* 图片预览区域 */}
              <AnimatePresence>
                {imagePreviews.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-4 gap-3 mb-3"
                  >
                    {imagePreviews.map((preview, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        className="relative aspect-square rounded-xl overflow-hidden border border-zinc-200 group"
                      >
                        <img
                          src={preview}
                          alt={`预览 ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                        {/* 删除按钮 */}
                        <button
                          type="button"
                          onClick={() => handleRemoveImage(index)}
                          className="absolute top-1 right-1 w-6 h-6 bg-zinc-900/70 hover:bg-zinc-900 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 上传按钮 */}
              {imagePreviews.length < MAX_IMAGES && (
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading}
                  className="flex items-center gap-2 px-4 py-3 border-2 border-dashed border-zinc-200 rounded-xl text-zinc-500 hover:border-emerald-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
                >
                  {isUploading ? (
                    <Spinner className="w-5 h-5 animate-spin" />
                  ) : (
                    <ImageIcon className="w-5 h-5" />
                  )}
                  <span className="text-sm">
                    {isUploading ? '上传中...' : '添加图片'}
                  </span>
                </button>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg,image/gif,image/webp"
                multiple
                onChange={handleImageSelect}
                className="hidden"
              />
              
              <p className="text-xs text-zinc-400 mt-2">
                支持 JPG、PNG、GIF、WebP 格式，单张最大 5MB
              </p>
            </div>

            <div>
              <label className="text-sm text-zinc-600 mb-2 block">
                联系邮箱 <span className="text-zinc-400">（选填）</span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="如需回复，请留下您的邮箱"
                className="w-full px-4 py-3 bg-white border border-zinc-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
              />
            </div>
          </div>

          {/* 提示信息 */}
          <div className="flex items-start gap-3 p-4 bg-blue-50 rounded-xl border border-blue-100">
            <ChatCircleText className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              您的反馈对我们非常重要。我们会认真阅读每一条反馈，并在产品迭代中持续改进。
              如果您有紧急问题，请联系客服 support@dataflow.engine
            </p>
          </div>

          {/* 提交按钮 */}
          <button
            type="submit"
            disabled={isSubmitting || !title.trim() || !content.trim()}
            className="w-full py-3.5 bg-zinc-900 text-white font-medium rounded-xl hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isSubmitting ? (
              <>
                <Spinner className="w-4 h-4 animate-spin" />
                提交中...
              </>
            ) : (
              <>
                <PaperPlaneRight className="w-4 h-4" weight="fill" />
                提交反馈
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}
